import { useState } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

interface StandardNoticesProps {
  autoReplyMessage?: string;
  oneWayNotificationMessage?: string;
}

const CONTENT_ID = "standard-notices-content";

// Boilerplate notices are collapsed by default so the composer stays focused on
// the SMS the user is writing. Full text is available on click, not on hover.
const StandardNotices = ({
  autoReplyMessage,
  oneWayNotificationMessage,
}: StandardNoticesProps) => {
  const { t } = useTranslation("message");
  const [expanded, setExpanded] = useState(false);

  const enabledNotices = [
    autoReplyMessage ? t("messageBox.notices.autoReplyName") : null,
    oneWayNotificationMessage ? t("messageBox.notices.oneWayName") : null,
  ].filter(Boolean);

  return (
    <Box
      className="mb-2 p-1"
      sx={(theme) => ({
        // Warning-tinted notice surface. A fixed light amber left the
        // text.secondary label invisible in dark mode.
        backgroundColor: alpha(theme.palette.warning.main, 0.2),
        borderRadius: "0.25rem",
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          noWrap={!expanded}
          sx={{ color: "text.secondary", minWidth: 0, fontWeight: 600 }}
        >
          {expanded
            ? t("messageBox.notices.enabledTitle")
            : t("messageBox.notices.enabledSummary", {
                notices: enabledNotices.join(" + "),
              })}
        </Typography>
        <Button
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          endIcon={
            expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )
          }
          aria-expanded={expanded}
          aria-controls={CONTENT_ID}
          sx={{ textTransform: "none", flexShrink: 0, py: 0 }}
        >
          {expanded
            ? t("messageBox.notices.collapse")
            : t("messageBox.notices.viewFullText")}
        </Button>
      </Box>

      {expanded && (
        <Box id={CONTENT_ID} sx={{ mt: 0.5 }}>
          {autoReplyMessage && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              <strong>{t("messageBox.autoReplyMessage")}</strong>{" "}
              {autoReplyMessage}
            </Typography>
          )}
          {oneWayNotificationMessage && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: autoReplyMessage ? 0.5 : 0,
              }}
            >
              <strong>{t("messageBox.oneWayReply")}</strong>{" "}
              {oneWayNotificationMessage}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StandardNotices;
